// XXX: ONE resolver for "which component is this element bound to", and it reads
// XXX: the STORED ID ONLY. Binding by name is gone: a name lookup silently
// XXX: repoints an element when the component is renamed, makes two components
// XXX: sharing a name a coin flip, and hid the fact that a binding was never
// XXX: really made. An element with no stored id is NOT BOUND -- broken data --
// XXX: and must fail loudly (hard throw on a compiled surface, red + warning in
// XXX: the builder's layers panel) so the designer repoints it.
// XXX: The name lookup survives in exactly ONE place: DROP time, where
// XXX: relatedComponentNameForDef picks the id a fresh drop should store. Once
// XXX: stored, the id is the binding forever. See task #58.
function boundComponentIdFromStored(storedId = 0) {
  const stored = Number(storedId);
  return Number.isFinite(stored) && stored > 0 ? stored : 0;
}

// The one wording for "this element has no usable component binding", shared by
// the staging throw, the builder console error and the layers-panel red row, so
// the three can never describe the same fault differently.
function unboundComponentMessage({ label = "element", elementId = "unknown", dynamicComponentId = "unknown" } = {}) {
  return `The element "${label}" (id ${elementId}) on DC ${dynamicComponentId} is not bound to a `
    + "component. Every binding is by component id; this element has none stored. Pick a "
    + "component for it in the inspector, or remove the element.";
}

const UNBOUND_MARKER = " is not bound to a component.";
const HEADING_UNBOUND = "Broken element — no component picked";
const HEADING_DEFAULT = "Broken element — context misconfiguration";

function isUnboundComponentMessage(text) {
  return typeof text === "string" && text.includes(UNBOUND_MARKER);
}

// Task 142: an unbound host is a missing binding, not a context problem; say which.
function brokenElementHeading(problems = []) {
  return problems.length > 0 && problems.every(isUnboundComponentMessage) ? HEADING_UNBOUND : HEADING_DEFAULT;
}

// XXX: The layers panel works with DynamicElement objects, not rendered Vue
// XXX: instances, so it cannot read the mixin's computed. This is the shared
// XXX: detector: is this element one that binds to a component, and if so does
// XXX: it actually have one? Same wording as the render-time failure, so the
// XXX: red row and the staging error page say the same thing.
const FeedComponentsMap = require("./../classes/feed-components-map");
const FEED_ELEMENT_TYPE = "DynamicAppFeedElement";

// XXX: FeedV2Element EXTENDS DynamicAppFeedElement, so a type-string check misses
// XXX: it -- which is how the reported feed stayed white in the layers panel.
// XXX: Walk the extends chain instead so every feed subclass is covered.
function isFeedElement(element) {
  if (!element) {
    return false;
  }
  for (let def = element.elementDef; def; def = def.extends) {
    if (def.name === FEED_ELEMENT_TYPE) {
      return true;
    }
  }
  const options = element.parsedOptions;
  return Boolean(FeedComponentsMap.legacyPayloadIn(options))
    || Boolean(FeedComponentsMap.readOptionValue(options));
}

function componentBindingError(element, dynamicComponentId = "unknown") {
  if (!isComponentBoundElement(element)) {
    return "";
  }
  const stored = element?.getRawOptionValue?.("referenced-component-id", "default", "default")?.r;
  if (boundComponentIdFromStored(stored)) {
    return "";
  }
  return unboundComponentMessage({
    label: element?.displayName
      || element?.elementDef?.elementPanelSettings?.displayName
      || element?.type
      || "element",
    elementId: element?.id ?? "unknown",
    dynamicComponentId,
  });
}

// Component-bound = it declares a related component name (the eleven hosts) or
// it IS the generic Component element.
function isComponentBoundElement(element) {
  if (!element) {
    return false;
  }
  if (element.type === "DynamicComponentElement") {
    return true;
  }
  return Boolean(relatedComponentNameForDef(element.elementDef, element.type));
}

// XXX: STATIC mirror of DcDependencyWalker::HOST_TYPE_COMPONENT_NAMES (PHP).
// XXX: The runtime element registry cannot be relied on here: elementDef is
// XXX: null for a host on a page route, because the GLOBAL cumulative registry
// XXX: is only populated once some DC computes its own registry (the same gap
// XXX: behind tasks #44 and #47). Depending on it meant the name fallback could
// XXX: not fire at all, so the inspector reported "no contexts required" for a
// XXX: component that requires one.
// XXX: tests/unit/builder/components/every-component-host-binds-its-component-by-id.test.mjs already pins that every
// XXX: statically-named host appears in the PHP map; this is the JS side of the
// XXX: same contract and must stay in step with it.
const HOST_TYPE_COMPONENT_NAMES = {
  PageHeader: "Page Header",
  PageFooter: "Page Footer",
  UserMenuAndJoin: "User Menu And Join",
  SubscribeToNewsletter: "Subscribe To Newsletter",
  CommentsWidget: "Comments Widget",
  EpisodeCommentsWidget: "Comments Widget",
  CollectionItemsCommentsWidget: "Collection Items Comments Widget",
  DigitalProductsCommentsWidget: "Digital Products Comments Widget",
};

// The default component name a host element declares. Prefers the live element
// definition (which also covers hosts whose default name is computed) and falls
// back to the static map when the registry has not been populated.
function relatedComponentNameForDef(elementDef, elementType = "") {
  const names = elementDef?.relatedComponents;
  if (Array.isArray(names) && names.length) {
    return String(names[0]);
  }
  return HOST_TYPE_COMPONENT_NAMES[elementType] ?? "";
}

// XXX: A feed's misconfiguration must reach errorsForElement, or the layers row
// XXX: stays white while the canvas paints a red broken-element card.
function feedMapError(element) {
  if (!isFeedElement(element)) {
    return "";
  }
  const options = element.parsedOptions;
  return FeedComponentsMap.problemsForOptions(options, FeedComponentsMap.readOptionValue(options))[0] ?? "";
}

module.exports = {
  boundComponentIdFromStored,
  componentBindingError,
  feedMapError,
  isComponentBoundElement,
  unboundComponentMessage,
  isUnboundComponentMessage,
  brokenElementHeading,
  relatedComponentNameForDef,
  HOST_TYPE_COMPONENT_NAMES,
};
module.exports.default = module.exports;
